import React, {
  useState,
  useEffect,
  useMemo,
  createContext,
  useRef,
} from 'react';
import { getItem, setItem } from '@/utils/localStorage';
import { getGlobalContext } from '@/utils/window';
import i18n from '@/utils/i18n';

export const locales: Record<string, string> = {
  de: 'de',
  en: 'en',
};

const defaultLocale = locales.de;

type LanguageContextValue = {
  locale: string;
  setLocale: (locale: string, onDone?: () => void) => void;
};

export const LanguageContext = createContext<LanguageContextValue>({
  locale: defaultLocale,
  setLocale: (locale: string, onDone?: () => void) => {
    console.log('still loading...');
    if (onDone) onDone();
  },
});

type LanguageContextProviderProps = {
  children: any;
};

function LanguageContextProvider({ children }: LanguageContextProviderProps) {
  const params = new URLSearchParams(window.location.search);
  const localeParam = params.get('locale');

  const initialLocale = useMemo(() => {
    const storedLocale = getItem('langLocale');
    const navLang = navigator.language.substring(0, 2);

    if (localeParam) {
      return locales[localeParam];
    }

    return storedLocale || locales[navLang] || defaultLocale;
  }, [localeParam]);

  (getGlobalContext() as Record<string, unknown>).pickpackLocale =
    params.get('locale');

  const [locale, setLocaleState] = useState(initialLocale);
  const changeCallbackRef = useRef<(() => void) | null>(null);

  const handleSetLocale = (nextLocale: string, onDone?: () => void) => {
    changeCallbackRef.current = onDone ?? null;
    setLocaleState(nextLocale);
  };

  useEffect(() => {
    const langLocale = locale || defaultLocale;
    const storedLocale = getItem('langLocale');
    if (storedLocale !== langLocale) {
      setItem('langLocale', langLocale);
    }

    // Ensure i18n language change completes before invoking any consumer callback
    i18n.changeLanguage(langLocale, () => {
      const callback = changeCallbackRef.current;
      changeCallbackRef.current = null;
      if (callback) callback();
    });
  }, [locale]);

  return (
    <LanguageContext.Provider value={{ locale, setLocale: handleSetLocale }}>
      {children}
    </LanguageContext.Provider>
  );
}

export default LanguageContextProvider;
